import { IDictionary, baseUrl, createGuid, devtools, extend } from '@bimser/core';
import axios, { AxiosPromise, AxiosRequestConfig } from 'axios';
import { onRefreshToken } from '../../utilities/initializeServices';
import {
	Canceler,
	ErrorInfo,
	HTTPRequestConfig,
	HTTPRequestResponse,
	InstanceList,
	ValidationErrorInfo,
	WrapResponse
} from './entities';
import * as ErrorCodes from './entities/errorCodes';
import { getErrorInfo } from './entities/ErrorInfo';
import { ClientCacheManager } from './helper';
import { Mobile } from '../../utilities/MobileView';

declare global {
	interface Window {
		ajax: {
			instances: IDictionary<HTTPRequest>;
			defaultConfig: HTTPRequestConfig;
			defaultHeaders: IDictionary<any>;
		};
		geoLocation: any;
	}
}

if (!window.ajax) {
	window.ajax = {
		instances: {},
		defaultConfig: {},
		defaultHeaders: {}
	};
}

const loginEndpoints: string[] = [
	'Login',
	'LoginWithAccessToken',
	'LoginWithDigitalSignature',
	'LoginWithExternalUser',
	'CreateTokenFromAuthCode'
];

export default class HTTPRequest {
	public static get instances(): IDictionary<HTTPRequest> {
		return window.ajax.instances;
	}

	public static get defaultConfig(): HTTPRequestConfig {
		return window.ajax.defaultConfig;
	}

	public static get defaultHeaders(): IDictionary<any> {
		return window.ajax?.defaultHeaders;
	}

	config: HTTPRequestConfig;

	tryCount: number = 0;

	static refreshTokenPromise: Promise<any>;

	constructor(config?: HTTPRequestConfig) {
		this.config = this.mergeConfig(config);

		Mobile.MobileView.setFunctionForMobile(
			Mobile.MobileFunctions.SetToken,
			(token?: string) => {
				return setNewToken(token);
			}
		);
	}

	head<T>(url: string, config?: HTTPRequestConfig): Promise<HTTPRequestResponse<T>> {
		let _url = url;
		const footprintGuid = createGuid();
		const executer = () => {
			let _config = this.mergeConfig(config);
			_url = baseUrl(url, _config.baseURL);
			_config = this.addHeaderConfig(_config, footprintGuid);

			const originConfig = this.getAxiosRequestConfig(_config);
			const source = axios.CancelToken.source();
			originConfig.cancelToken = source.token;

			if (config) {
				(config as any).canceler = source.cancel;
			}

			if (config?.controller) {
				originConfig.signal = config.controller.signal;
			}

			return axios.head(url, originConfig);
		};
		return this.executeAxios(executer, _url, footprintGuid);
	}

	getExecuterConfig(config, footPrint) {
		let _config = this.mergeConfig(config);
		_config = this.addHeaderConfig(_config, footPrint);

		const originConfig = this.getAxiosRequestConfig(_config);
		const source = axios.CancelToken.source();
		originConfig.cancelToken = source.token;

		if (config) {
			(config as any).canceler = source.cancel;
		}

		if (config?.controller) {
			originConfig.signal = config.controller.signal;
		}

		return originConfig;
	}

	get<T>(url: string, config?: HTTPRequestConfig): Promise<HTTPRequestResponse<T>> {
		const footprintGuid = createGuid();
		const executer = () => {
			const originConfig = this.getExecuterConfig(config, footprintGuid);
			return axios.get<WrapResponse<T>>(url, originConfig);
		};
		const promise = this.executeAxios(executer, url, footprintGuid);
		return this.executeXhr<T>(promise, url, executer, footprintGuid);
	}

	post<T>(url: string, data?: any, config?: HTTPRequestConfig): Promise<HTTPRequestResponse<T>> {
		const footprintGuid = createGuid();

		const executer = async () => {
			const originConfig = this.getExecuterConfig(config, footprintGuid);
			const relationId = config?.clientCacheId;

			if (config?.useClientCachedData) {
				const clientCache = ClientCacheManager.getCache(
					relationId,
					ClientCacheManager.createHash(url, data)
				);
				if (clientCache) {
					try {
						const result = await clientCache.result;
						return result;
					} catch (err) {
						ClientCacheManager.cleanCache(relationId);
					}
				}
			}

			if (config?.controller) {
				originConfig.signal = config.controller.signal;
			}

			const resolver = axios.post<WrapResponse<T>>(url, data, originConfig);

			if (config?.useClientCachedData) {
				ClientCacheManager.setCache({ relationId, url, payload: data, result: resolver });
			}

			return resolver;
		};

		const promise = this.executeAxios(executer, url, footprintGuid);
		return this.executeXhr<T>(promise, url, executer, footprintGuid);
	}

	delete<T>(
		url: string,
		data?: any,
		config?: HTTPRequestConfig
	): Promise<HTTPRequestResponse<T>> {
		const footprintGuid = createGuid();

		const executer = () => {
			const originConfig = this.getExecuterConfig(config, footprintGuid);
			return axios.delete<WrapResponse<T>>(url, originConfig);
		};
		const promise = this.executeAxios(executer, url, footprintGuid);
		return this.executeXhr<T>(promise, url, executer, footprintGuid);
	}

	put<T>(url: string, data?: any, config?: HTTPRequestConfig): Promise<HTTPRequestResponse<T>> {
		const footprintGuid = createGuid();

		const executer = () => {
			const originConfig = this.getExecuterConfig(config, footprintGuid);
			return axios.put<WrapResponse<T>>(url, data, originConfig);
		};
		const promise = this.executeAxios(executer, url, footprintGuid);
		return this.executeXhr<T>(promise, url, executer, footprintGuid);
	}

	static setHeaders(headers: any) {
		if (headers) {
			Object.keys(headers).forEach(key => {
				window.ajax.defaultHeaders[key] = headers[key];
			});
		}
	}

	static get<T>(url: string, config?: HTTPRequestConfig): Promise<HTTPRequestResponse<T>> {
		const inst = new HTTPRequest(config);
		return inst.get(url, config);
	}

	static head<T>(url: string, config?: HTTPRequestConfig): Promise<HTTPRequestResponse<T>> {
		const inst = new HTTPRequest(config);
		return inst.head(url, config);
	}

	static post<T>(
		url: string,
		data?: any,
		config?: HTTPRequestConfig
	): Promise<HTTPRequestResponse<T>> {
		const inst = new HTTPRequest(config);
		return inst.post(url, data, config);
	}

	static delete<T>(
		url: string,
		data?: any,
		config?: HTTPRequestConfig
	): Promise<HTTPRequestResponse<T>> {
		const inst = new HTTPRequest(config);
		return inst.delete(url, data, config);
	}

	static put<T>(
		url: string,
		data?: any,
		config?: HTTPRequestConfig
	): Promise<HTTPRequestResponse<T>> {
		const inst = new HTTPRequest(config);
		return inst.put(url, data, config);
	}

	static createInstances<T extends IDictionary<HTTPRequestConfig>>(configs: T): InstanceList<T> {
		for (const key in configs) {
			const inst = new HTTPRequest(configs[key]);
			window.ajax.instances[key] = inst;
		}

		return <InstanceList<T>>window.ajax.instances;
	}

	static getInstances<T extends IDictionary<HTTPRequestConfig>>(): InstanceList<T> {
		return <InstanceList<T>>window.ajax.instances;
	}

	private mergeConfig(config: HTTPRequestConfig) {
		return extend(true, {}, window.ajax.defaultConfig, this.config, config, {
			headers: window.ajax.defaultHeaders
		});
	}

	private addHeaderConfig(config: HTTPRequestConfig, footPrint: string): HTTPRequestConfig {
		const geoLocationString = window.geoLocation
			? `{lat:${window.geoLocation.lat},lon:${window.geoLocation.lon}}`
			: undefined;

		return extend(
			true,
			{},
			{
				headers: {
					['Bimser-FootPrint']: footPrint,
					['Bimser-Geolocation']: geoLocationString,
					['Bimser-DevTools']: devtools.isOpen
				}
			},
			config
		);
	}

	private getAxiosRequestConfig(config: HTTPRequestConfig): AxiosRequestConfig {
		return {
			baseURL: config.baseURL,
			data: config.data,
			headers: config.headers,
			onDownloadProgress: config.onDownloadProgress,
			onUploadProgress: config.onUploadProgress,
			params: config.params
		};
	}

	private executeAxios(
		sendRequest: () => Promise<any>,
		url: string,
		footprintGuid: string
	): Promise<any> {
		if (!!HTTPRequest.refreshTokenPromise) {
			return new Promise((resolve, reject) => {
				HTTPRequest.refreshTokenPromise
					.then(() => {
						sendRequest()
							.then(response => {
								this.createResponse(response, resolve, reject, url, footprintGuid);
							})
							.catch(error => {
								//refreshToken'dan sonra yine token expire hatası geldiyse logine yönlendirmesi için 994 kodu fırlatılıyor.
								//let errCode = error.response.data.errorDetail.code === "FRMW_002" ? ErrorCodes.RefReshTokenSecurityError : error.response.data.errorDetail.code
								const errorDetail = error.response.data?.errorDetail || {};

								const {
									code = null,
									message = null,
									internalMessage = null,
									exception = null
								} = errorDetail;

								const _error: ErrorInfo = getErrorInfo({
									url,
									code,
									message,
									internalMessage,
									exception,
									hasValidation: !!errorDetail?.validations_?.length,
									status: error.response.status,
									footPrint: footprintGuid
								});
								reject(_error);
							});
						HTTPRequest.refreshTokenPromise = undefined;
					})
					.catch(reject);
			});
		} else {
			return sendRequest();
		}
	}

	private createResponse<T>(
		response: any,
		resolve: any,
		reject: any,
		url: string,
		footprintGuid: string
	) {
		if (response.data) {
			const bcResponse: HTTPRequestResponse<T> = {
				data: response.data.result ?? response.data,
				headers: response.headers
			};
			resolve(bcResponse);
		} else {
			const error: ErrorInfo = getErrorInfo({
				url,
				code: ErrorCodes.ResponseIsNullError,
				message: 'Response is null',
				internalMessage: 'Response is null',
				footPrint: footprintGuid
			});
			reject(error);
		}
	}

	private executeXhr<T>(
		promise: AxiosPromise<WrapResponse<T>>,
		url: string,
		sendRequest: () => Promise<any>,
		footprintGuid: string
	): Promise<HTTPRequestResponse<T>> {
		const executer = () =>
			new Promise<HTTPRequestResponse<T>>((resolve, reject) => {
				promise
					.then(response => {
						this.createResponse(response, resolve, reject, url, footprintGuid);
					})
					.catch(error => {
						if (error.response) {
							if (error.response.status == 401) {
								if (
									loginEndpoints.findIndex(endpoint => url.endsWith(endpoint)) >
									-1
								) {
									//Login endpointlerinden gelen 401 hataları için refresh token adımları çalıştırılmayacak.
									const errorInfo: ErrorInfo = getErrorInfo({
										url,
										code:
											ErrorCodes.OutOf2xxError +
											' - ' +
											error.response.status,
										message: error.message,
										internalMessage: error,
										exception: error.response,
										footPrint: footprintGuid
									});
									reject(errorInfo);
								} else if (
									error.response.data?.exception?.Code === 'FRMW_ASPN_0006'
								) {
									reject(
										getErrorInfo({
											url,
											code: 'FRMW_ASPN_0006',
											message: error.message,
											internalMessage: error,
											exception: error.response,
											footPrint: footprintGuid
										})
									);
								} else if (
									error.response.data?.exception?.Code === 'FRMW_CORE_0043'
								) {
									reject(
										getErrorInfo({
											url,
											code: 'FRMW_CORE_0043',
											message: error.message,
											internalMessage: error,
											exception: error.response,
											footPrint: footprintGuid
										})
									);
								} else if (error.response.data?.errorDetail?.code === 'FRMW_006') {
									const { message, internalMessage, response } =
										error.response.data.errorDetail;
									const errorInfo: ErrorInfo = getErrorInfo({
										url,
										code: ErrorCodes.RefReshTokenSecurityError,
										message,
										internalMessage,
										exception: response,
										footPrint: footprintGuid
									});
									reject(errorInfo);
								} else if (!!HTTPRequest.refreshTokenPromise) {
									if (
										url.split('/').lastIndexOf('Refresh') ===
										url.split('/').length - 1
									) {
										const errorDetail = error.response.data?.errorDetail
											? error.response.data.errorDetail
											: {};
										const errorInfo: ErrorInfo = getErrorInfo({
											url,
											code: ErrorCodes.RefReshTokenSecurityError,
											message: errorDetail.message,
											internalMessage: errorDetail.internalMessage,
											exception: errorDetail.response,
											footPrint: footprintGuid
										});
										reject(errorInfo);
									} else {
										resolve(this.executeAxios(sendRequest, url, footprintGuid));
									}
								} else {
									HTTPRequest.refreshTokenPromise = onRefreshToken();

									HTTPRequest.refreshTokenPromise
										.then(response => {
											setNewToken(response.token).then(() => {
												resolve(
													this.executeAxios(
														sendRequest,
														url,
														footprintGuid
													)
												);
											});
										})
										.catch(response => {
											if (response.code === '994') {
												reject(response);
											} else {
												const errorInfo: ErrorInfo = getErrorInfo({
													url,
													code: ErrorCodes.RefReshTokenError,
													message: response.message,
													internalMessage: response,
													exception: response.response,
													footPrint: footprintGuid
												});
												reject(errorInfo);
											}

											Mobile.MobileView.authorizationCheckPoint({
												type: 'RefreshToken',
												isValid: false,
												token: null
											});
										});
								}
							} else if (error.response.status == 502) {
								this.tryCount++;
								if (this.tryCount <= 3) {
									// Try again
									const delay = this.tryCount * 5000;
									console.log(
										`Encountered a 502 timeout error while attempting to access ${url}. Initiating a retry in ${
											delay / 1000
										} seconds.`
									);
									setTimeout(() => {
										executer()
											.then(resolve)
											.catch(e => reject(e));
									}, delay);
								} else {
									const errorInfo: ErrorInfo = getErrorInfo({
										url,
										code:
											ErrorCodes.OutOf2xxError +
											' - ' +
											error.response.status,
										message: error.message,
										internalMessage: error,
										exception: error.response,
										footPrint: footprintGuid
									});
									reject(errorInfo);
								}
							} else {
								const errorInfo: ErrorInfo = getErrorInfo({
									url,
									code: ErrorCodes.OutOf2xxError + ' - ' + error.response.status,
									message: error.message,
									internalMessage: error,
									exception: error.response,
									footPrint: footprintGuid
								});
								reject(errorInfo);
							}
						} else if (error.request) {
							// The request was made but no response was received `error.request` is an instance of XMLHttpRequest in the browser and an instance of http.ClientRequest in node.js
							const errorInfo: ErrorInfo = getErrorInfo({
								url,
								code: ErrorCodes.NoResponseError,
								message: error.message,
								internalMessage: error,
								exception: error.request,
								footPrint: footprintGuid
							});
							reject(errorInfo);
						} else {
							// Something happened in setting up the request that triggered an Error
							const errorInfo: ErrorInfo = getErrorInfo({
								url,
								code: ErrorCodes.RequestSettingUpError,
								message: error.message,
								internalMessage: error,
								exception: error,
								footPrint: footprintGuid
							});
							reject(errorInfo);
						}
					});
			});

		return executer();
	}
}

const setNewToken = (token: string) => {
	return new Promise<void>(resolve => {
		HTTPRequest.setHeaders({
			Authorization: 'Bearer ' + token
		});
		sessionStorage.setItem('token', token);
		if (localStorage.getItem('rememberMe') == '1') {
			localStorage.setItem('token', token);
		}

		resolve();

		Mobile.MobileView.authorizationCheckPoint({
			type: 'RefreshToken',
			isValid: true,
			token: token
		});
	});
};

export {
	Canceler,
	ClientCacheManager,
	ErrorInfo,
	HTTPRequestConfig,
	HTTPRequestResponse,
	ValidationErrorInfo,
	WrapResponse
};
